import { useState, useEffect } from 'react'

import {
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  DndContext,
  rectIntersection,
  DragOverlay,
  defaultDropAnimationSideEffects
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable'
import _ from 'lodash'
import { createPortal } from 'react-dom'
import { useFormContext } from 'react-hook-form'

import { LevelPlayAtThePlaceBoard } from './Board'
import { LevelPlayAtThePlaceGhostWord } from './Word'
import { useBorderColor } from '../../hooks/useBorderColor'

export const LevelPlayAtThePlace = ({
  activity
}) => {
  const { setValue } = useFormContext()
  const { borderColor } = useBorderColor()
  const { meta } = activity
  const associations = _.get(meta, 'associations')
  const leftAssociationExpressions = _.get(
    meta,
    'associations.0.expressions',
    []
  )
  const rightAssociationsExpressions = _.get(
    meta,
    'associations.1.expressions',
    []
  )
  const allWords = _.shuffle(
    _.concat(leftAssociationExpressions, rightAssociationsExpressions)
  ).filter(item => item !== '')
  const newWords = []
  const separate = allWords.length / 2
  for (var i = 0; i < allWords.length; i = i + separate) {
    newWords.push(allWords.slice(i, i + separate))
  }
  const [items, setItems] = useState({
    rightAssociationsExpressions: newWords[0],
    leftAssociationExpressions: newWords[1]
  })
  const [activeId, setActiveId] = useState(null)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const findContainer = id => {
    if (id in items) {
      return id
    }

    return Object.keys(items).find(key => items[key].includes(id))
  }
  const handleDragStart = event => {
    const { active } = event
    const { id } = active

    setActiveId(id)
  }
  const handleDragOver = ({ active, over }) => {
    const overId = over?.id

    if (overId == null || active.id in items) {
      return
    }
    const overContainer = findContainer(overId)
    const activeContainer = findContainer(active.id)

    if (!overContainer || !activeContainer) {
      return
    }

    if (activeContainer !== overContainer) {
      setItems(items => {
        const activeItems = items[activeContainer]
        const overItems = items[overContainer]
        const overIndex = overItems.indexOf(overId)
        const activeIndex = activeItems.indexOf(active.id)

        let newIndex

        if (overId in items) {
          newIndex = overItems.length + 1
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height

          const modifier = isBelowOverItem ? 1 : 0

          newIndex =
            overIndex >= 0 ? overIndex + modifier : overItems.length + 1
        }

        return {
          ...items,
          [activeContainer]: items[activeContainer].filter(
            item => item !== active.id
          ),
          [overContainer]: [
            ...items[overContainer].slice(0, newIndex),
            items[activeContainer][activeIndex],
            ...items[overContainer].slice(newIndex, items[overContainer].length)
          ]
        }
      })
    }
  }
  const handleDragEnd = ({ active, over }) => {
    if (active.id in items && over?.id) {
      setContainers(containers => {
        const activeIndex = containers.indexOf(active.id)
        const overIndex = containers.indexOf(over.id)

        return arrayMove(containers, activeIndex, overIndex)
      })
    }

    const activeContainer = findContainer(active.id)

    if (!activeContainer) {
      setActiveId(null)
      return
    }

    const overId = over?.id

    if (overId == null) {
      setActiveId(null)
      return
    }
    const overContainer = findContainer(overId)

    if (overContainer) {
      const activeIndex = items[activeContainer].indexOf(active.id)
      const overIndex = items[overContainer].indexOf(overId)

      if (activeIndex !== overIndex) {
        setItems(items => ({
          ...items,
          [overContainer]: arrayMove(
            items[overContainer],
            activeIndex,
            overIndex
          )
        }))
      }
    }

    setActiveId(null)
  }

  useEffect(() => {
    setValue('answer', items)
  }, [items])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="grid gap-1 w-full mx-auto grid-cols-2">
        {associations.map((association, index) => {
          return (
            <div
              key={`association-${index}`}
              className={`overflow-hidden border-3 rounded-xl flex flex-col items-center ${borderColor}`}
            >
              <div className="aspect-square w-full rounded-t-lg overflow-hidden">
                <img
                  src={_.get(association, 'image.file.url')}
                  alt={_.get(association, 'name')}
                  className="object-cover object-center bg-white block w-full h-full"
                />
              </div>
              <span className="text-gray-800 font-extrabold text-sm md:text-lg w-full text-center block p-4">
                {_.get(association, 'name')}
              </span>
              <div className="flex w-full gap-3 grow">
                <LevelPlayAtThePlaceBoard
                  key={
                    index == 0
                      ? 'leftAssociationExpressions'
                      : 'rightAssociationsExpressions'
                  }
                  id={
                    index == 0
                      ? 'leftAssociationExpressions'
                      : 'rightAssociationsExpressions'
                  }
                  items={
                    index == 0
                      ? items['leftAssociationExpressions']
                      : items['rightAssociationsExpressions']
                  }
                />
              </div>
            </div>
          )
        })}
      </div>
      {createPortal(
        <DragOverlay adjustScale={false} dropAnimation={dropAnimation}>
          <div style={{ 'touch-action': 'none' }}>
            {activeId ? (
              <LevelPlayAtThePlaceGhostWord
                id={activeId}
              />
            ) : null}
          </div>
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  )
}
const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5'
      }
    }
  })
}
